import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { toast } from 'sonner';
import {
  getInventory,
  getUnopenedPoolOffers,
  listPoolShippings,
  sellPoolItem,
  sellInventoryItems,
  type ApiClient,
  type IframePoolOrderItem,
  type IframePoolShipping,
  type IframeUnopenedOffer,
} from '../../modules/api';
import { useInfinitePagination } from '../../modules/pagination';
import { useWallet } from '../../modules/wallet';
import { notifyBuyback } from '../../modules/notifications';
import { Backdrop } from '../../modules/design-system';
import { RepackzTab } from './tabs/RepackzTab';
import { DropsTab } from './tabs/DropsTab';
import { DeliveriesTab } from './tabs/DeliveriesTab';

const PER_PAGE = 30;
// Background poll for the active tab. Belt-and-suspenders next to the
// visibilitychange reload: keeps a long-lived, focused tab from drifting stale
// (e.g. a card sold in another tab) even without a tab switch. Only runs while
// the tab is visible, so idle background tabs never poll.
const REFETCH_INTERVAL_MS = 30_000;
// Detail modal disabled — card tap now toggles selection. Keep the import
// path commented out so we can restore the flow without re-discovering it.
// import { OpenedItemModal } from './cards/OpenedItemModal';
import { MyPacksHeader, type MyPacksTabDef, type MyPacksTabId } from './MyPacksHeader';
import { MyPacksStatsRow } from './MyPacksStatsRow';
import { MyPacksToolbar } from './MyPacksToolbar';
import { MyPacksFooter } from './MyPacksFooter';
import { ShipDisclaimerModal } from './ShipDisclaimerModal';
import { SellingOverlay } from './SellingOverlay';
import { DeliveryNewPage, DeliveryShowPage } from '../Delivery';
import { useMyPacksRoute } from './myPacksRoute';
import { useShippingDisabled } from '../../modules/shipping';
import { useSession } from '../../modules/session';
import { useBackHandler } from '../../modules/backStack';

const TABS: MyPacksTabDef[] = [
  // UI label is "Packs"; internal tab id keeps the `repackz` name
  // (matches backend terminology / existing call sites).
  { id: 'repackz', label: 'Packs' },
  // UI label stays "Cards" per Figma; internal tab id keeps the
  // `drops` name (matches backend terminology / existing call sites).
  { id: 'drops', label: 'Cards' },
  { id: 'deliveries', label: 'Shipping' },
];

export interface MyPacksPageProps {
  client: ApiClient;
  onBack: () => void;
  onOpenPack: (offer: IframeUnopenedOffer) => void;
  /** Cards-tab card-modal "Buy Another Repack" — switches to that
   *  pool_offer's pack page (parent wires this to setGameId / setView). */
  onBuyAnother?: (poolOfferId: number) => void;
  /** Drop the noise-SVG overlay from the page gradient. Propagated to
   *  the Delivery New / Show pages so the whole flow looks consistent. */
  hideBackgroundPattern?: boolean;
  /** Items handed in from an external entry (the reveal "Confirm Shipping"
   *  CTA) to seed the create-delivery flow. Used once for that delivery,
   *  then the Cards-tab selection drives subsequent ones. */
  deliverySeed?: IframePoolOrderItem[];
}

/**
 * My Packs — three tabs over a dark glass page. Figma 706:1683.
 *
 *   Repackz    — pool_offers with unopened packs, one "Open Now" per row.
 *   Cards      — opened pool_items grid (was "Drops" before the redesign),
 *                with select-all/ship/submit placeholders, a 3-stat header
 *                row and per-card buyback CTA.
 *   Deliveries — empty state — shipping lives on the main site.
 *
 * Background gradient + every visible colour flows through Design
 * Constructor knobs (`--co-MyPacks*-*`); see `packages/theme-constructor/src/schema/components.ts`.
 */
export function MyPacksPage({
  client,
  onBack,
  onOpenPack,
  hideBackgroundPattern = false,
  deliverySeed,
}: MyPacksPageProps) {
  const [tab, setTab] = useState<MyPacksTabId>('drops');
  // Detail-modal state disabled while card tap toggles selection.
  // const [activeItem, setActiveItem] = useState<IframePoolOrderItem | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // One-shot delivery seed from the reveal CTA — captured at mount, used for
  // the delivery it opened, then cleared so a later Cards-tab delivery uses
  // the Cards selection.
  const [seedItems, setSeedItems] = useState<IframePoolOrderItem[] | null>(
    deliverySeed && deliverySeed.length > 0 ? deliverySeed : null,
  );
  const [shipModalOpen, setShipModalOpen] = useState(false);
  const { route, push: navigate } = useMyPacksRoute();
  const wallet = useWallet();
  const session = useSession();

  const disableShipping = useShippingDisabled();

  // Log out is offered only to embed self-registered players — the ones who
  // signed up through the iframe (SessionContext.embedKey is set). Operator-
  // and partner-provided sessions launch with a session_token and no embedKey,
  // so their `onLogout` stays undefined and the control never renders.
  const handleLogout = useCallback(() => {
    // Leave My Packs for the home/pack page first (a reload would re-read the
    // my-packs URL and land back here), then drop the token → anonymous.
    onBack();
    session.dropToken();
  }, [onBack, session]);

  // Hide the Shipping tab entirely when shipping is disabled; also guard
  // against a stale `tab` value landing on the deliveries panel.
  const visibleTabs = disableShipping ? TABS.filter((t) => t.id !== 'deliveries') : TABS;
  const activeTab = disableShipping && tab === 'deliveries' ? 'drops' : tab;

  // Block deep-links to the create-delivery route when shipping is disabled.
  useEffect(() => {
    if (disableShipping && route.kind === 'delivery-new') {
      navigate({ kind: 'index' });
    }
  }, [disableShipping, route.kind, navigate]);

  // Host back button (modules/backStack): a delivery sub-route steps back to
  // the index, same as its own Back button.
  useBackHandler(
    route.kind !== 'index',
    () => {
      if (route.kind === 'delivery-new') setSeedItems(null);
      navigate({ kind: 'index' });
    },
    'subroute',
  );

  // Three paginated feeds — Repackz (unopened offers), Drops (opened items)
  // and Deliveries (pool_shippings). All live at the page level so the
  // lists survive tab switches; a tab revisit refetches in the background
  // (stale-while-revalidate) instead of flashing skeletons. Fetchers are
  // memoised so the hook's bootstrap effect doesn't loop on every parent
  // render.
  const unopenedFetcher = useCallback(
    (page: number, per: number) =>
      getUnopenedPoolOffers(client, { page, per }).then((res) => ({
        items: res.offers,
        totalCount: res.totalCount,
      })),
    [client],
  );
  const inventoryFetcher = useCallback(
    (page: number, per: number) =>
      getInventory(client, { page, per }).then((res) => ({
        items: res.items,
        totalCount: res.totalCount,
      })),
    [client],
  );
  const shippingsFetcher = useCallback(
    (page: number, per: number) =>
      listPoolShippings(client, { page, per }).then((res) => ({
        items: res.shippings,
        totalCount: res.meta.totalCount,
      })),
    [client],
  );

  const repackz = useInfinitePagination<IframeUnopenedOffer>(unopenedFetcher, {
    per: PER_PAGE,
  });
  const drops = useInfinitePagination<IframePoolOrderItem>(inventoryFetcher, {
    per: PER_PAGE,
  });
  const deliveries = useInfinitePagination<IframePoolShipping>(shippingsFetcher, {
    per: PER_PAGE,
  });

  // Reload the visited tab's feed on every tab switch (the mount run is
  // skipped — each hook bootstraps itself). Needed because the backend
  // settles mutations asynchronously: e.g. right after a shipping is created
  // the shipped cards may still be present in the inventory response, so the
  // list must be re-requested when the user comes back to the tab. The reload
  // happens behind the current list (refetch is stale-while-revalidate) and
  // behind an empty state too, because the tabs skeleton on `initialLoading`
  // rather than on `loading`.
  const repackzRefetch = repackz.refetch;
  const dropsRefetch = drops.refetch;
  const deliveriesRefetch = deliveries.refetch;
  const refetchActiveTab = useCallback(() => {
    if (activeTab === 'repackz') void repackzRefetch();
    else if (activeTab === 'drops') void dropsRefetch();
    else if (activeTab === 'deliveries') void deliveriesRefetch();
  }, [activeTab, repackzRefetch, dropsRefetch, deliveriesRefetch]);

  const skipMountRefetchRef = useRef(true);
  useEffect(() => {
    if (skipMountRefetchRef.current) {
      skipMountRefetchRef.current = false;
      return;
    }
    refetchActiveTab();
  }, [refetchActiveTab]);

  // Keep the active tab fresh so a card sold in another tab disappears here —
  // the backend rejects the double sale, but a leftover sold card is confusing
  // and invites the attempt. Two triggers, both silent (they reload behind
  // whatever the tab already shows, list or empty state), both scoped to the
  // active tab and to a visible page so idle background tabs never poll:
  //   • visibilitychange — reloads the instant the user returns to the tab.
  //   • 30s interval — catches a long-lived focused tab that never switches.
  useEffect(() => {
    const refetchIfVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refetchActiveTab();
    };
    document.addEventListener('visibilitychange', refetchIfVisible);
    const intervalId = setInterval(refetchIfVisible, REFETCH_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', refetchIfVisible);
      clearInterval(intervalId);
    };
  }, [refetchActiveTab]);

  const selectedItems = useMemo(
    () => drops.items.filter((i) => selected.has(i.id)),
    [drops.items, selected],
  );

  const handleItemToggle = useCallback((item: IframePoolOrderItem) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
  }, []);

  const selectableIds = useMemo(
    () =>
      drops.items
        .filter((i) => (i.bestActiveOffer?.grossPrice ?? 0) > 0)
        .map((i) => i.id),
    [drops.items],
  );

  const refetchAfterSell = useCallback(() => {
    void drops.refetch();
  }, [drops]);

  // Per-item in-flight latch — blocks a fast double-tap on one card's Sell
  // CTA from firing two sellPoolItem POSTs (and two buyback toasts). A POST
  // is never coalesced by the client's GET dedup, so the guard lives here.
  const sellingItemIdsRef = useRef(new Set<IframePoolOrderItem['id']>());

  const handleItemSell = useCallback(
    async (item: IframePoolOrderItem) => {
      if (!item.poolOrderId) return;
      if (sellingItemIdsRef.current.has(item.id)) return;
      sellingItemIdsRef.current.add(item.id);
      // Snapshot the gross before selling — the backend nulls the offer out.
      const gross = item.bestActiveOffer?.grossPrice ?? 0;
      try {
        await sellPoolItem(client, item.poolOrderId, item.id);
        notifyBuyback(gross);
        void wallet.refresh({ expectCredit: true });
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to sell card',
        );
      } finally {
        sellingItemIdsRef.current.delete(item.id);
      }
      // Refetch on failure too: a card that can no longer be sold (e.g. it
      // is committed to a delivery) must drop its stale buyback pill.
      refetchAfterSell();
    },
    [client, refetchAfterSell, wallet],
  );

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (submitting || selected.size === 0) return;

    // Snapshot each card's gross price BEFORE selling — the backend nulls out
    // best_active_offer once a card is sold, so the payout can't be read after
    // the refetch. Keyed by id so we can sum only the cards that actually sell.
    const grossById = new Map(
      drops.items.map((i) => [i.id, i.bestActiveOffer?.grossPrice ?? 0]),
    );

    // Select All only marks the currently-loaded page (selectableIds is
    // derived from drops.items), so an explicit id list would silently skip
    // every un-paginated card. When the selection matches the loaded
    // selectable set, switch to `{ all: true }` so the backend sells the
    // user's entire inventory, not just what's on screen.
    const allSelectable =
      selectableIds.length > 0 && selected.size === selectableIds.length;

    setSubmitting(true);
    try {
      const result = await sellInventoryItems(
        client,
        allSelectable
          ? { all: true }
          : { poolItemIds: Array.from(selected) },
      );

      if (result.sold.length > 0) {
        // Sum only the cards that actually sold so a partial failure doesn't
        // inflate the toast total.
        const payout = result.sold.reduce(
          (sum, item) => sum + (grossById.get(item.id) ?? 0),
          0,
        );
        notifyBuyback(payout);
      }
      if (result.failed.length > 0) {
        const failedLabel = result.failed.length === 1 ? 'card' : 'cards';
        toast.error(`${result.failed.length} ${failedLabel} couldn't be sold`);
      }
      // sold === 0 && failed === 0: nothing changed — stay silent.

      setSelected(new Set());
      void wallet.refresh({ expectCredit: result.sold.length > 0 });
      refetchAfterSell();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to sell cards');
    } finally {
      setSubmitting(false);
    }
  }, [
    client,
    drops.items,
    refetchAfterSell,
    selectableIds,
    selected,
    submitting,
    wallet,
  ]);

  if (route.kind === 'delivery-new' && !disableShipping) {
    return (
      <DeliveryNewPage
        client={client}
        selectedItems={seedItems ?? selectedItems}
        hideBackgroundPattern={hideBackgroundPattern}
        onBack={() => {
          setSeedItems(null);
          navigate({ kind: 'index' });
        }}
        onSuccess={() => {
          // Shipping created — back to My Packs and pivot to the
          // Deliveries tab. Refetch its feed explicitly (the hook lives at
          // page level, so there's no remount; and the tab may already be
          // active, in which case the tab-switch effect won't fire).
          // Also refetch Drops: the shipped cards are no longer sellable and
          // must drop off the Cards tab together with their buyback pills.
          setSeedItems(null);
          setSelected(new Set());
          setTab('deliveries');
          void deliveries.refetch();
          refetchAfterSell();
          navigate({ kind: 'index' });
        }}
      />
    );
  }

  if (route.kind === 'delivery-show') {
    return (
      <DeliveryShowPage
        client={client}
        shippingId={route.id}
        hideBackgroundPattern={hideBackgroundPattern}
        onBack={() => navigate({ kind: 'index' })}
      />
    );
  }

  return (
    <div
      data-page="my-packs"
      style={
        hideBackgroundPattern
          ? ({ '--co-MyPacksPageBackground-noiseOpacity': 0 } as CSSProperties)
          : undefined
      }
    >
      <Backdrop bgImageUrl={null} />
      {/* Optional photo layer above the grey gradient backdrop. When the
          `bgImage` knob is unset the CSS var resolves to `none` and this
          div contributes nothing; otherwise the `imageUrl` formatter
          ships a `url("…")` value rendered at cover-size, centred. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          backgroundImage: 'var(--co-MyPacksPageBackground-bgImage, none)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      <div
        data-co-component="MyPacksPageBackground"
        className="@container relative flex min-h-screen w-full flex-col"
      >
        <MyPacksHeader
          tabs={visibleTabs}
          active={activeTab}
          onChange={(id) => {
            // Tab panels share the window scroll — reset it so each tab
            // opens at the top, same as a page navigation.
            window.scrollTo(0, 0);
            setTab(id);
          }}
          onHome={onBack}
          {...(!disableShipping && { onAddDelivery: () => navigate({ kind: 'delivery-new' }) })}
          {...(session.embedKey && { onLogout: handleLogout })}
        />

        <div className="mx-auto flex w-full max-w-350 flex-1 flex-col">
        {activeTab === 'drops' && (
          <div
            data-co-component="MyPacksStatsBar"
            // Surface fill is applied via inline `background` (shorthand)
            // so a ColorField hex from the Design Constructor swatch
            // cleanly replaces the default `transparent`. Border + radius
            // stay on container-query Tailwind arbitrary classes so the
            // mobile-no-border / tablet+border responsive design is
            // preserved (the var() fallback at each breakpoint keeps the
            // pre-existing pixel values).
            className="
              mx-4 my-4
              flex flex-col gap-3
              @[600px]:mx-6 @[600px]:py-3 @[600px]:px-4
              @[600px]:[border-radius:var(--co-MyPacksStatsBar-borderRadius,24px)]
              @[600px]:[border:var(--co-MyPacksStatsBar-borderWidth,1px)_solid_var(--co-MyPacksStatsBar-borderColor,rgba(242,242,242,0.10))]
              @[900px]:h-[52px] @[900px]:flex-row
              @[900px]:items-center @[900px]:justify-between
              @[900px]:gap-3 @[900px]:[border-radius:var(--co-MyPacksStatsBar-borderRadius,40px)] @[900px]:py-0 @[900px]:pl-4 @[900px]:pr-2
            "
            style={{ background: 'var(--co-MyPacksStatsBar-fill, transparent)' }}
          >
            <MyPacksStatsRow
              inventory={drops.items}
              packsOpened={drops.totalCount}
            />
            <MyPacksToolbar
              totalSelectable={selectableIds.length}
              selectedCount={selected.size}
              onSelectAll={() => setSelected(new Set(selectableIds))}
              onClear={() => setSelected(new Set())}
              onShip={() => {
                if (selected.size === 0) return;
                setShipModalOpen(true);
              }}
              onSubmit={handleSubmit}
              submitting={submitting}
              canShip={!disableShipping}
            />
          </div>
        )}

        <main
          className={
            'flex-1 px-4 @[600px]:px-6 @[600px]:pb-12 ' +
            (activeTab === 'drops' ? 'pb-32' : 'pb-12')
          }
        >
          {activeTab === 'repackz' && (
            <RepackzTab
              offers={repackz.items}
              initialLoading={repackz.initialLoading}
              loadingMore={repackz.loadingMore}
              reachedEnd={repackz.reachedEnd}
              sentinelRef={repackz.sentinelRef}
              onOpen={onOpenPack}
              onBrowse={onBack}
            />
          )}
          {activeTab === 'drops' && (
            <DropsTab
              items={drops.items}
              initialLoading={drops.initialLoading}
              loadingMore={drops.loadingMore}
              reachedEnd={drops.reachedEnd}
              sentinelRef={drops.sentinelRef}
              onBrowse={onBack}
              onItemClick={handleItemToggle}
              onItemSell={handleItemSell}
              selectedIds={selected}
              selling={submitting}
            />
          )}
          {activeTab === 'deliveries' && (
            <DeliveriesTab
              shippings={deliveries.items}
              initialLoading={deliveries.initialLoading}
              loadingMore={deliveries.loadingMore}
              reachedEnd={deliveries.reachedEnd}
              error={deliveries.error}
              onRetry={() => void deliveries.refetch()}
              sentinelRef={deliveries.sentinelRef}
              onOpenDelivery={(id) =>
                navigate({ kind: 'delivery-show', id })
              }
            />
          )}
        </main>

        {/* Drops tab has a mobile-only fixed bottom toolbar (Select All
         *  / Buyback All); reserve room under the footer so the bar
         *  doesn't cover the legal links. Toolbar is `@[600px]:hidden`,
         *  so the spacer is gated the same way. */}
        <div className={activeTab === 'drops' ? 'pb-20 @[600px]:pb-0' : ''}>
          <MyPacksFooter client={client} />
        </div>
        </div>
      </div>

      <SellingOverlay visible={submitting} />

      {shipModalOpen && (
        <ShipDisclaimerModal
          client={client}
          poolItemIds={[...selected]}
          onClose={() => setShipModalOpen(false)}
          onAcceptBuyback={() => {
            setShipModalOpen(false);
            void handleSubmit();
          }}
          onAddDelivery={() => {
            setShipModalOpen(false);
            navigate({ kind: 'delivery-new' });
          }}
        />
      )}

      {/* Detail modal disabled — card tap now toggles selection. Restore
          when we want the per-card sell/buy-another flow back.
      {activeItem && (
        <OpenedItemModal
          client={client}
          item={activeItem}
          onClose={() => setActiveItem(null)}
          onSold={() => {
            void data.refetch();
          }}
          onBuyAnother={(offerId) => {
            setActiveItem(null);
            if (onBuyAnother) onBuyAnother(offerId);
            else onBack();
          }}
        />
      )}
      */}
    </div>
  );
}
